import type { z } from 'zod'
import { time } from '~/services/api/contracts/time'
import { Normalization } from '~/utils/normalization'

const isFilled = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0)

type ScheduleEntry = ReturnType<typeof Normalization.listOfAssocArrays>[number]

const issue = (ctx: z.RefinementCtx, message: string, path: (string | number)[]): void => {
  ctx.addIssue({ code: 'custom', message, path })
}

const isCustomMode = (value: Record<string, unknown>): boolean => {
  const explicitMode = value['availability_mode']
  const implied =
    isFilled(value['custom_hours']) ||
    isFilled(value['schedule']) ||
    value['availability'] === 'time-based'
  return explicitMode === 'custom' || (explicitMode !== 'sync' && implied)
}

const checkSeasonalDates = (value: Record<string, unknown>, ctx: z.RefinementCtx): void => {
  if (value['availability'] === 'seasonal') {
    if (!isFilled(value['available_from'])) {
      issue(ctx, 'The available_from field is required for seasonal menus', ['available_from'])
    }
    if (!isFilled(value['available_until'])) {
      issue(ctx, 'The available_until field is required for seasonal menus', ['available_until'])
    }
  }
}

const checkEnabledEntry = (entry: ScheduleEntry, index: number, ctx: z.RefinementCtx): void => {
  if (!time.validate(entry['start_time'])) {
    issue(ctx, 'time_invalid', ['custom_hours', index, 'start_time'])
  }
  if (!time.validate(entry['end_time'])) {
    issue(ctx, 'time_invalid', ['custom_hours', index, 'end_time'])
  }
}

const checkCustomEntries = (schedule: ScheduleEntry[], ctx: z.RefinementCtx): void => {
  let enabled = 0
  schedule.forEach((entry, index) => {
    if (entry['is_enabled'] === true) {
      enabled += 1
      checkEnabledEntry(entry, index, ctx)
    }
  })
  if (enabled === 0) {
    issue(ctx, 'At least one enabled day is required when availability_mode is custom', [
      'custom_hours'
    ])
  }
}

/** Equal start/end means open 24 hours; end earlier than start is overnight — neither is an ordering error. */
export const menuScheduleRules = (
  value: Record<string, unknown>,
  issues: z.RefinementCtx
): void => {
  const availability = value['availability']
  const schedule = isFilled(value['custom_hours'])
    ? Normalization.listOfAssocArrays(value['custom_hours'])
    : []
  const hasSchedule = schedule.length > 0
  const isCustom = isCustomMode(value)

  if ((availability === 'time-based' || isCustom) && !hasSchedule) {
    issue(issues, 'At least one custom schedule entry is required for custom menu availability', [
      'custom_hours'
    ])
  }

  checkSeasonalDates(value, issues)

  if (
    hasSchedule &&
    typeof availability === 'string' &&
    availability !== 'time-based' &&
    isCustom
  ) {
    issue(issues, 'Availability must be time-based when a schedule is provided', ['availability'])
  }

  if (isCustom) {
    checkCustomEntries(schedule, issues)
  }
}
